import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Stack, Typography, Autocomplete, TextField, Button, Chip, CircularProgress, Snackbar, Alert,
  Grid, Tabs, Tab, Checkbox, MenuItem, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControlLabel, Switch, TablePagination,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReplayIcon from '@mui/icons-material/Replay';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ResponsiveTable from '../components/common/ResponsiveTable';
import ResponsiveDialog from '../components/common/ResponsiveDialog';
import apiService from '../services/api.service';

/**
 * Sales Portal > Print A/R Invoices and > Print Sales Orders: two separate pages (each with its
 * own module and permission, its own print jobs) on this one component.
 *
 * Search SAP documents, select one or many, and send them to the office printers: the Epson
 * LQ-2190 on the pre-printed form (ESC/P2), and, for sales orders, also a laser printer (plain A4
 * PDF). The backend renders and queues one job per document per printer; the print agent on the
 * office server picks the jobs up and sends them to the printer, so the Print jobs tab is where a
 * user sees whether each document actually printed. Printers are shared (Printer setup, Admin).
 */

export type PrintKind = 'invoice' | 'order';

interface KindSpec {
  title: string; tab: string; noun: string; nouns: string; numberLabel: string; notSetUp: string;
  /** Sales orders go to several printers at once (dot-matrix form and laser). */
  multiPrinter: boolean;
  api: {
    config: (companyId: number) => Promise<any>;
    customers: (companyId: number, search?: string) => Promise<any>;
    search: (companyId: number, filters: Record<string, string | undefined>) => Promise<any>;
    preview: (companyId: number, docEntry: number) => Promise<any>;
    print: (companyId: number, printerIds: number[], docs: Array<Record<string, unknown>>) => Promise<any>;
    jobs: (companyId: number, mine?: boolean) => Promise<any>;
    cancel: (companyId: number, jobId: number) => Promise<any>;
    reprint: (companyId: number, jobId: number) => Promise<any>;
  };
}

const KINDS: Record<PrintKind, KindSpec> = {
  invoice: {
    title: 'Print A/R Invoices', tab: 'Invoices', noun: 'invoice', nouns: 'invoices', numberLabel: 'Invoice No.',
    notSetUp: 'A/R invoice printing is not set up for this company.', multiPrinter: false,
    api: {
      config: (c) => apiService.getInvoicePrintConfig(c),
      customers: (c, s) => apiService.getInvoicePrintCustomers(c, s),
      search: (c, f) => apiService.searchInvoicesForPrint(c, f),
      preview: (c, d) => apiService.previewInvoicePrint(c, d),
      print: (c, p, d) => apiService.printInvoices(c, p[0], d),
      jobs: (c, m) => apiService.getInvoicePrintJobs(c, m),
      cancel: (c, j) => apiService.cancelInvoicePrintJob(c, j),
      reprint: (c, j) => apiService.reprintInvoicePrintJob(c, j),
    },
  },
  order: {
    title: 'Print Sales Orders', tab: 'Sales orders', noun: 'sales order', nouns: 'sales orders', numberLabel: 'Order No.',
    notSetUp: 'Sales order printing is not set up for this company.', multiPrinter: true,
    api: {
      config: (c) => apiService.getOrderPrintConfig(c),
      customers: (c, s) => apiService.getOrderPrintCustomers(c, s),
      search: (c, f) => apiService.searchOrdersForPrint(c, f),
      preview: (c, d) => apiService.previewOrderPrint(c, d),
      print: (c, p, d) => apiService.printOrders(c, p, d),
      jobs: (c, m) => apiService.getOrderPrintJobs(c, m),
      cancel: (c, j) => apiService.cancelOrderPrintJob(c, j),
      reprint: (c, j) => apiService.reprintOrderPrintJob(c, j),
    },
  },
};

interface CustomerOpt {
  cardCode: string; cardName: string;
  /** The first option while typing: every customer containing the typed text (cardName holds the text). */
  all?: boolean;
}
interface InvoiceRow {
  docEntry: number; docNum: number; docDate: string; taxDate: string; cardCode: string; cardName: string;
  numAtCard: string; docTotal: number; currency: string; cancelled: boolean; sapPrinted: boolean;
  /** Delivery date (DocDueDate) and open status: shown for sales orders. */
  dueDate?: string; open?: boolean;
  lastPrint?: { status: string; at: string; jobId: number } | null;
}
interface PrinterOpt { printerId: number; name: string; language?: 'ESCP' | 'PDF'; hasAgent: boolean; agentLastSeen: string | null }
interface PrintConfig { enabled: boolean; printers: PrinterOpt[]; viewAll: boolean; canManage: boolean }
interface JobRow {
  jobId: number; docEntry: number; docNum: string; cardCode: string; cardName: string; pages: number;
  status: 'pending' | 'queued' | 'printing' | 'printed' | 'failed' | 'cancelled'; error: string | null;
  createdDate: string; completedAt: string | null; printerName: string; createdByName: string | null;
}
interface PrinterRow {
  printerId: number; name: string; agentId: number | null; agentName: string | null; agentLastSeen: string | null;
  host: string; port: number; offsetXmm: number; offsetYmm: number; formLengthIn: number; barcodeMode: 'native' | 'graphic';
  quality: 'draft' | 'nlq'; language: 'ESCP' | 'PDF'; isActive: boolean;
}
interface AgentRow {
  agentId: number; name: string; tokenPrefix: string; isActive: boolean; lastSeenAt: string | null;
  lastIp: string | null; version: string | null;
}

const fmtMoney = (n: number) => Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const fmtDate = (s: string) => (s ? new Date(s).toLocaleDateString() : '—');
const fmtDateTime = (s: string | null) => (s ? new Date(s).toLocaleString() : '—');
const errMsg = (e: any, fallback: string) => e?.response?.data?.message || e?.message || fallback;

const STATUS_COLOR: Record<JobRow['status'], 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  pending: 'default', queued: 'info', printing: 'warning', printed: 'success', failed: 'error', cancelled: 'default',
};
/** 'pending': PRPO is still preparing the job (reading SAP, rendering) before the agent can take it. */
const statusLabel = (s: string) => (s === 'pending' ? 'preparing' : s);

/** An agent that has not polled for 2 minutes is probably not running. */
const agentOnline = (lastSeen: string | null) => !!lastSeen && Date.now() - new Date(lastSeen).getTime() < 120000;

const printerKind = (p: { language?: string }) => (p.language === 'PDF' ? 'laser, A4' : 'dot-matrix form');

function SalesDocumentPrint({ kind }: { kind: PrintKind }) {
  const spec = KINDS[kind];
  const companyId = useMemo(() => Number(localStorage.getItem('selectedCompanyId') || '') || undefined, []);
  const [config, setConfig] = useState<PrintConfig | null>(null);
  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' | 'info' } | null>(null);

  const loadConfig = useCallback(async () => {
    if (!companyId) return;
    try {
      const r: any = await spec.api.config(companyId);
      setConfig(r.data);
    } catch (e) {
      setToast({ msg: errMsg(e, 'Could not load the print settings'), sev: 'error' });
    }
  }, [companyId, spec]);

  useEffect(() => { void loadConfig(); }, [loadConfig]);

  if (!companyId) {
    return <Alert severity="info">Select a company first.</Alert>;
  }
  if (!config) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!config.enabled) {
    return (
      <Box sx={{ p: { xs: 1, md: 2 } }}>
        <Typography variant="h5" gutterBottom>{spec.title}</Typography>
        <Alert severity="info">{spec.notSetUp}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Typography variant="h5" component="h1" gutterBottom>{spec.title}</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" allowScrollButtonsMobile>
        <Tab label={spec.tab} />
        <Tab label="Print jobs" />
        {config.canManage && <Tab label="Printer setup" />}
      </Tabs>
      {tab === 0 && <InvoicesTab kind={kind} companyId={companyId} config={config} notify={setToast} onPrinted={() => setTab(1)} />}
      {tab === 1 && <JobsTab kind={kind} companyId={companyId} viewAll={config.viewAll} notify={setToast} />}
      {tab === 2 && config.canManage && <SetupTab companyId={companyId} notify={setToast} onChanged={loadConfig} />}
      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

type Notify = (t: { msg: string; sev: 'success' | 'error' | 'info' }) => void;

// ---- Invoices: search, select, preview, print -------------------------------------------

function InvoicesTab({ kind, companyId, config, notify, onPrinted }: {
  kind: PrintKind; companyId: number; config: PrintConfig; notify: Notify; onPrinted: () => void;
}) {
  const spec = KINDS[kind];
  const order = kind === 'order';
  const [customerOpts, setCustomerOpts] = useState<CustomerOpt[]>([]);
  // What is typed in the Customer box, and the term the list was last fetched for (typing only).
  const [customerInput, setCustomerInput] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  // The term the current list was fetched for, so the counts never belong to an older term.
  const [customerOptsTerm, setCustomerOptsTerm] = useState('');
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customer, setCustomer] = useState<CustomerOpt | null>(null);
  const [docNum, setDocNum] = useState('');
  const [numAtCard, setNumAtCard] = useState('');
  const [postingFrom, setPostingFrom] = useState('');
  const [postingTo, setPostingTo] = useState('');
  const [documentFrom, setDocumentFrom] = useState('');
  const [documentTo, setDocumentTo] = useState('');
  const [deliveryFrom, setDeliveryFrom] = useState('');
  const [deliveryTo, setDeliveryTo] = useState('');
  const [openOnly, setOpenOnly] = useState(true);
  const [rows, setRows] = useState<InvoiceRow[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [limit, setLimit] = useState(50000);
  // The list is shown 200 rows at a time; selecting works on every match.
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(200);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [printerId, setPrinterId] = useState<number | ''>(config.printers.length === 1 ? config.printers[0].printerId : '');
  // Sales orders: every printer ticked by default (billing prints each order on the form and on the laser).
  const [printerIds, setPrinterIds] = useState<number[]>(() => config.printers.map((p) => p.printerId));
  const [pdfLoading, setPdfLoading] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [preview, setPreview] = useState<{ docNum: number; pages: string[] } | null>(null);
  const [previewLoading, setPreviewLoading] = useState<number | null>(null);

  // Every customer whose code or name contains each typed word ("Coop" → every Coop branch).
  // A slower answer to an older term never replaces the list of a newer one.
  useEffect(() => {
    let stale = false;
    const t = setTimeout(async () => {
      setCustomersLoading(true);
      try {
        const r: any = await spec.api.customers(companyId, customerSearch.trim() || undefined);
        if (!stale) { setCustomerOpts(r.data || []); setCustomerOptsTerm(customerSearch.trim()); }
      } catch { /* the picker stays empty; search still works by other fields */ }
      finally { if (!stale) setCustomersLoading(false); }
    }, 300);
    return () => { stale = true; clearTimeout(t); };
  }, [companyId, customerSearch, spec]);

  const customerLabel = (o: CustomerOpt) => (o.all ? o.cardName : `${o.cardCode} — ${o.cardName}`);
  // Text typed without picking one customer searches every customer whose name contains it.
  const customerName = customer ? '' : customerInput.trim();
  const nameMatches = customerName && customerOptsTerm === customerName && !customersLoading ? customerOpts.length : null;
  const matchesLabel = (n: number) => `${n >= 500 ? '500+' : n} customer${n === 1 ? '' : 's'}`;
  // While typing, the list starts with "All customers containing <text>": choosing it lists the
  // documents of every one of them at once (e.g. every Coop branch).
  const allTerm = customer ? '' : customerSearch.trim();
  const pickerOptions: CustomerOpt[] = allTerm && customerOptsTerm === allTerm && customerOpts.length > 0
    ? [{ cardCode: '__all__', cardName: allTerm, all: true }, ...customerOpts]
    : customerOpts;

  const hasCriteria = !!(customer || customerName || docNum.trim() || numAtCard.trim() || postingFrom || postingTo
    || (order ? deliveryFrom || deliveryTo : documentFrom || documentTo));

  /** `allCustomersContaining`: search every customer containing this text (the "All customers" option). */
  const search = async (allCustomersContaining?: string) => {
    const name = allCustomersContaining ?? customerName;
    const code = allCustomersContaining ? undefined : customer?.cardCode;
    if (!hasCriteria && !name) {
      notify({ msg: 'Enter at least one search criterion', sev: 'info' });
      return;
    }
    setLoading(true);
    try {
      const r: any = await spec.api.search(companyId, {
        cardCode: code, customerName: name || undefined, docNum: docNum.trim() || undefined, numAtCard: numAtCard.trim() || undefined,
        postingFrom: postingFrom || undefined, postingTo: postingTo || undefined,
        ...(order
          ? { deliveryFrom: deliveryFrom || undefined, deliveryTo: deliveryTo || undefined, openOnly: openOnly ? 'true' : undefined }
          : { documentFrom: documentFrom || undefined, documentTo: documentTo || undefined }),
      });
      setRows(r.data || []);
      setTruncated(!!r.truncated);
      if (r.limit) setLimit(Number(r.limit));
      setPage(0);
      setSelected(new Set());
      setSearched(true);
    } catch (e) {
      notify({ msg: errMsg(e, 'Search failed'), sev: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const clear = () => {
    setCustomer(null); setCustomerInput(''); setCustomerSearch(''); setDocNum(''); setNumAtCard(''); setPostingFrom(''); setPostingTo('');
    setDocumentFrom(''); setDocumentTo(''); setDeliveryFrom(''); setDeliveryTo('');
    setRows([]); setSelected(new Set()); setSearched(false);
  };

  const toggle = (docEntry: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(docEntry)) next.delete(docEntry); else next.add(docEntry);
      return next;
    });
  };
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.docEntry));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.docEntry)));

  const openPreview = async (row: InvoiceRow) => {
    setPreviewLoading(row.docEntry);
    try {
      const r: any = await spec.api.preview(companyId, row.docEntry);
      setPreview(r.data);
    } catch (e) {
      notify({ msg: errMsg(e, 'Preview failed'), sev: 'error' });
    } finally {
      setPreviewLoading(null);
    }
  };

  // A sales order's laser printout, opened in a new tab.
  const openPdf = async (row: { docEntry: number; docNum: number }) => {
    setPdfLoading(row.docEntry);
    try {
      const blob = await apiService.getOrderPrintPdf(companyId, row.docEntry);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      notify({ msg: errMsg(e, 'Could not build the laser printout'), sev: 'error' });
    } finally {
      setPdfLoading(null);
    }
  };

  // Printed in document-number order, so the stack comes off the printer sorted.
  const selectedRows = rows.filter((r) => selected.has(r.docEntry)).sort((a, b) => a.docNum - b.docNum);
  const printer = config.printers.find((p) => p.printerId === printerId);
  const chosen = spec.multiPrinter
    ? config.printers.filter((p) => printerIds.includes(p.printerId))
    : config.printers.filter((p) => p.printerId === printerId);
  const togglePrinter = (id: number) => setPrinterIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const print = async () => {
    if (chosen.length === 0) return;
    setPrinting(true);
    try {
      const r: any = await spec.api.print(companyId, chosen.map((p) => p.printerId),
        selectedRows.map((x) => ({ docEntry: x.docEntry, docNum: x.docNum, cardCode: x.cardCode, cardName: x.cardName })));
      const { jobs, failed } = r.data;
      setConfirmOpen(false);
      setSelected(new Set());
      const docs = new Set(jobs.map((j: any) => j.docEntry)).size;
      if (failed.length > 0) {
        notify({ msg: `${docs} queued; ${failed.length} could not be queued: ${failed.slice(0, 5).map((f: any) => f.error).join('; ')}${failed.length > 5 ? '…' : ''}`, sev: 'error' });
      } else {
        notify({ msg: `${docs} ${spec.noun}(s) queued for ${chosen.map((p) => p.name).join(' and ') || 'the printer'}: PRPO prepares them in number order and the printer takes each as soon as it is ready`, sev: 'success' });
      }
      if (jobs.length > 0) onPrinted();
    } catch (e) {
      notify({ msg: errMsg(e, 'Printing failed'), sev: 'error' });
    } finally {
      setPrinting(false);
    }
  };

  const lastPrintChip = (r: InvoiceRow) => {
    if (!r.lastPrint) return <Typography variant="body2" color="text.secondary">—</Typography>;
    const s = r.lastPrint.status as JobRow['status'];
    return <Chip size="small" color={STATUS_COLOR[s] || 'default'} label={`${statusLabel(s)} ${fmtDate(r.lastPrint.at)}`} />;
  };

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={pickerOptions}
              value={customer}
              onChange={(_, v) => {
                if (v?.all) {
                  // Every customer containing the text: list their documents now.
                  setCustomer(null);
                  setCustomerInput(v.cardName);
                  void search(v.cardName);
                  return;
                }
                setCustomer(v);
                if (v) setCustomerInput(customerLabel(v));
              }}
              inputValue={customerInput}
              onInputChange={(_, v, reason) => {
                if (reason === 'input') {
                  setCustomerInput(v);
                  setCustomerSearch(v);
                  // Editing the text after picking a customer goes back to a name search.
                  if (customer && v !== customerLabel(customer)) setCustomer(null);
                } else if (reason === 'clear') {
                  setCustomerInput(''); setCustomerSearch(''); setCustomer(null);
                } else {
                  setCustomerInput(v);
                }
              }}
              // Keep what was typed when the box loses focus: it is a search term on its own.
              clearOnBlur={false}
              getOptionLabel={customerLabel}
              isOptionEqualToValue={(a, b) => a.cardCode === b.cardCode}
              filterOptions={(x) => x}
              // Enter takes the highlighted first option: "All customers containing ..." while typing.
              autoHighlight
              renderOption={(props, o) => {
                const { key, ...rest } = props as typeof props & { key: string };
                return o.all ? (
                  <li key={key} {...rest} style={{ ...rest.style, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>All customers containing "{o.cardName}"</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {matchesLabel(customerOpts.length)} — lists all their {spec.nouns}
                      </Typography>
                    </Box>
                  </li>
                ) : (
                  <li key={key} {...rest}>{customerLabel(o)}</li>
                );
              }}
              loading={customersLoading}
              noOptionsText={customerSearch.trim() ? `No customer contains "${customerSearch.trim()}"` : 'Type part of a customer name'}
              renderInput={(p) => (
                <TextField {...p} label="Customer (BP)" size="small" placeholder="Type part of a name, e.g. Coop"
                  helperText={customerName
                    ? `All customers containing "${customerName}"` + (nameMatches === null ? '' : ` (${matchesLabel(nameMatches)})`)
                      + ` — Search lists all their ${spec.nouns}`
                    : undefined} />
              )}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField label={spec.numberLabel} size="small" fullWidth value={docNum}
              onChange={(e) => setDocNum(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => { if (e.key === 'Enter') void search(); }}
              inputProps={{ inputMode: 'numeric' }} />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField label="Customer Ref. No." size="small" fullWidth value={numAtCard}
              onChange={(e) => setNumAtCard(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void search(); }} />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField label="Posting date from" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }}
              value={postingFrom} onChange={(e) => setPostingFrom(e.target.value)} />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField label="Posting date to" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }}
              value={postingTo} onChange={(e) => setPostingTo(e.target.value)} />
          </Grid>
          {order ? (
            <>
              <Grid item xs={6} md={3}>
                <TextField label="Delivery date from" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }}
                  value={deliveryFrom} onChange={(e) => setDeliveryFrom(e.target.value)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField label="Delivery date to" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }}
                  value={deliveryTo} onChange={(e) => setDeliveryTo(e.target.value)} />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={6} md={3}>
                <TextField label="Document date from" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }}
                  value={documentFrom} onChange={(e) => setDocumentFrom(e.target.value)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField label="Document date to" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }}
                  value={documentTo} onChange={(e) => setDocumentTo(e.target.value)} />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="contained" startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                onClick={() => void search()} disabled={loading}>Search</Button>
              <Button onClick={clear} disabled={loading}>Clear</Button>
              {order && (
                <FormControlLabel sx={{ ml: 1 }} control={<Switch checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />}
                  label="Open orders only" />
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {searched && (
        <Paper sx={{ p: 2 }}>
          {truncated && (
            <Alert severity="warning" sx={{ mb: 1 }}>Showing the newest {limit} matches. Narrow the search to see the rest.</Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
            <FormControlLabel
              control={<Checkbox checked={allSelected} indeterminate={selected.size > 0 && !allSelected} onChange={toggleAll} />}
              label={`${rows.length} ${spec.noun}(s)${selected.size ? ` · ${selected.size} selected` : ''}`}
            />
            <Box sx={{ flex: 1 }} />
            {spec.multiPrinter ? (
              <Box>
                {config.printers.length === 0 && <Typography variant="body2" color="error">No printer set up yet</Typography>}
                {config.printers.map((p) => (
                  <FormControlLabel key={p.printerId}
                    control={<Checkbox size="small" checked={printerIds.includes(p.printerId)} onChange={() => togglePrinter(p.printerId)} />}
                    label={(
                      <Typography variant="body2" color={agentOnline(p.agentLastSeen) ? 'text.primary' : 'error'}>
                        {p.name} <Typography component="span" variant="caption" color="text.secondary">({printerKind(p)}{agentOnline(p.agentLastSeen) ? '' : ', agent offline'})</Typography>
                      </Typography>
                    )} />
                ))}
              </Box>
            ) : (
              <TextField select size="small" label="Printer" value={printerId} sx={{ minWidth: 220 }}
                onChange={(e) => setPrinterId(Number(e.target.value))}
                helperText={config.printers.length === 0 ? 'No printer set up yet' : printer && !agentOnline(printer.agentLastSeen) ? 'Print agent looks offline' : ' '}
                error={!!printer && !agentOnline(printer.agentLastSeen)}>
                {config.printers.map((p) => <MenuItem key={p.printerId} value={p.printerId}>{p.name}</MenuItem>)}
              </TextField>
            )}
            <Button variant="contained" startIcon={<PrintIcon />} disabled={selected.size === 0 || chosen.length === 0}
              onClick={() => setConfirmOpen(true)}>
              Print {selected.size || ''}
            </Button>
          </Stack>
          <ResponsiveTable<InvoiceRow>
            ariaLabel={order ? 'Sales orders' : 'A/R invoices'}
            rows={rows.slice(page * pageSize, (page + 1) * pageSize)}
            getRowKey={(r) => r.docEntry}
            emptyMessage={`No ${spec.nouns} match.`}
            size="small"
            columns={[
              {
                id: 'sel', header: '', width: 48,
                cell: (r) => <Checkbox size="small" checked={selected.has(r.docEntry)} onChange={() => toggle(r.docEntry)}
                  inputProps={{ 'aria-label': `Select ${spec.noun} ${r.docNum}` }} />,
              },
              { id: 'docNum', header: spec.numberLabel, cell: (r) => <>{r.docNum}{r.cancelled && <Chip size="small" label="Cancelled" sx={{ ml: 1 }} />}</> },
              { id: 'docDate', header: 'Posting date', cell: (r) => fmtDate(r.docDate) },
              order
                ? { id: 'dueDate', header: 'Delivery date', cell: (r: InvoiceRow) => fmtDate(r.dueDate || '') }
                : { id: 'taxDate', header: 'Document date', cell: (r: InvoiceRow) => fmtDate(r.taxDate), hideOnMobile: true },
              { id: 'customer', header: 'Customer', cell: (r) => <><b>{r.cardCode}</b> {r.cardName}</> },
              { id: 'ref', header: 'Customer Ref.', cell: (r) => r.numAtCard || '—', hideOnMobile: true },
              { id: 'total', header: 'Total', align: 'right', cell: (r) => `${fmtMoney(r.docTotal)} ${r.currency}` },
              ...(order
                ? [{ id: 'open', header: 'Status', cell: (r: InvoiceRow) => <Chip size="small" variant="outlined" label={r.open ? 'Open' : 'Closed'} />, hideOnMobile: true }]
                : []),
              { id: 'last', header: 'Last print', cell: lastPrintChip, hideOnMobile: true },
              {
                id: 'preview', header: '', align: 'right',
                cell: (r) => (
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Tooltip title={order ? 'Dot-matrix preview' : 'Preview'}>
                      <span>
                        <IconButton size="small" onClick={() => void openPreview(r)} disabled={previewLoading !== null} aria-label={`Preview ${spec.noun} ${r.docNum}`}>
                          {previewLoading === r.docEntry ? <CircularProgress size={18} /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    {order && (
                      <Tooltip title="Laser printout (PDF)">
                        <span>
                          <IconButton size="small" onClick={() => void openPdf(r)} disabled={pdfLoading !== null} aria-label={`Laser printout of ${spec.noun} ${r.docNum}`}>
                            {pdfLoading === r.docEntry ? <CircularProgress size={18} /> : <PictureAsPdfIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                ),
              },
            ]}
          />
          {rows.length > pageSize && (
            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[100, 200, 500]}
              labelRowsPerPage="Rows per page"
            />
          )}
        </Paper>
      )}

      <Dialog open={confirmOpen} onClose={() => !printing && setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Print {selectedRows.length} {spec.noun}(s)?</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>{chosen.length > 1 ? 'Printers' : 'Printer'}: <b>{chosen.map((p) => p.name).join(', ')}</b></Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {order
              ? `${chosen.some((p) => p.language !== 'PDF') ? 'Load the pre-printed forms in the dot-matrix first. ' : ''}Orders print in number order:`
              : 'Load the pre-printed invoice forms first. Invoices print in number order:'}
          </Typography>
          <Typography variant="body2" sx={{ maxHeight: 160, overflow: 'auto' }}>
            {selectedRows.map((r) => r.docNum).join(', ')}
          </Typography>
          {selectedRows.length > 50 && (
            <Alert severity="info" sx={{ mt: 1 }}>
              PRPO prepares a large run in the background: the jobs appear in Print jobs and print one after another. You can leave this page.
            </Alert>
          )}
          {selectedRows.some((r) => r.lastPrint?.status === 'printed') && (
            <Alert severity="warning" sx={{ mt: 1 }}>Some of these {spec.nouns} were already printed from PRPO.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={printing}>Cancel</Button>
          <Button variant="contained" onClick={() => void print()} disabled={printing}
            startIcon={printing ? <CircularProgress size={18} color="inherit" /> : <PrintIcon />}>Print</Button>
        </DialogActions>
      </Dialog>

      <ResponsiveDialog open={!!preview} onClose={() => setPreview(null)} maxWidth="lg" fullWidth>
        <DialogTitle>{order ? 'Sales order' : 'Invoice'} {preview?.docNum}: {order ? 'dot-matrix preview' : 'print preview'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {order
              ? 'The values as they land on the form, one page per continuous form. The laser printout is the PDF button in the list.'
              : 'The values as they land on the form, one page per continuous form. The printer draws the barcode itself.'}
          </Typography>
          {preview?.pages.map((p, i) => (
            <Box key={i} component="pre" sx={{
              fontFamily: 'monospace', fontSize: 11, lineHeight: 1.25, p: 1.5, mb: 2, overflowX: 'auto',
              border: '1px dashed', borderColor: 'divider', bgcolor: 'background.default',
            }}>{p}</Box>
          ))}
        </DialogContent>
        <DialogActions><Button onClick={() => setPreview(null)}>Close</Button></DialogActions>
      </ResponsiveDialog>
    </Stack>
  );
}

// ---- Print jobs ---------------------------------------------------------------------

function JobsTab({ kind, companyId, viewAll, notify }: { kind: PrintKind; companyId: number; viewAll: boolean; notify: Notify }) {
  const spec = KINDS[kind];
  const Noun = spec.noun.charAt(0).toUpperCase() + spec.noun.slice(1);
  const [rows, setRows] = useState<JobRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [mine, setMine] = useState(!viewAll);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r: any = await spec.api.jobs(companyId, mine);
      setRows(r.data || []);
    } catch (e) {
      notify({ msg: errMsg(e, 'Could not load print jobs'), sev: 'error' });
    } finally {
      setLoading(false);
    }
  }, [companyId, mine, notify, spec]);

  useEffect(() => { void load(); }, [load]);

  // Follow jobs while PRPO prepares them or the agent is still working on them.
  const active = rows.some((r) => r.status === 'pending' || r.status === 'queued' || r.status === 'printing');
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => { void load(); }, 4000);
    return () => clearInterval(t);
  }, [active, load]);

  const act = async (row: JobRow, action: 'cancel' | 'reprint') => {
    setBusy(row.jobId);
    try {
      if (action === 'cancel') await spec.api.cancel(companyId, row.jobId);
      else await spec.api.reprint(companyId, row.jobId);
      notify({ msg: action === 'cancel' ? 'Job cancelled' : `${Noun} ${row.docNum} queued again`, sev: 'success' });
      await load();
    } catch (e) {
      notify({ msg: errMsg(e, 'Action failed'), sev: 'error' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        {viewAll && <FormControlLabel control={<Switch checked={mine} onChange={(e) => setMine(e.target.checked)} />} label="Only my jobs" />}
        <Box sx={{ flex: 1 }} />
        <Button startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />} onClick={() => void load()}>Refresh</Button>
      </Stack>
      <ResponsiveTable<JobRow>
        ariaLabel="Print jobs"
        rows={rows}
        getRowKey={(r) => r.jobId}
        emptyMessage="No print jobs yet."
        size="small"
        columns={[
          { id: 'job', header: 'Job', cell: (r) => r.jobId, hideOnMobile: true },
          { id: 'doc', header: spec.numberLabel, cell: (r) => r.docNum },
          { id: 'cust', header: 'Customer', cell: (r) => <><b>{r.cardCode}</b> {r.cardName}</> },
          { id: 'printer', header: 'Printer', cell: (r) => r.printerName, hideOnMobile: true },
          { id: 'pages', header: kind === 'order' ? 'Pages' : 'Forms', align: 'right', cell: (r) => r.pages, hideOnMobile: true },
          {
            id: 'status', header: 'Status',
            cell: (r) => (
              <Tooltip title={r.error || ''}>
                <Chip size="small" color={STATUS_COLOR[r.status]} label={statusLabel(r.status)} />
              </Tooltip>
            ),
          },
          { id: 'error', header: 'Message', cell: (r) => r.error || '', hideOnMobile: true },
          { id: 'when', header: 'Sent', cell: (r) => fmtDateTime(r.createdDate), hideOnMobile: true },
          { id: 'by', header: 'By', cell: (r) => r.createdByName || '—', hideOnMobile: true },
          {
            id: 'act', header: '', align: 'right',
            cell: (r) => (
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                {(r.status === 'queued' || r.status === 'pending') && (
                  <Tooltip title="Cancel"><span><IconButton size="small" disabled={busy !== null} onClick={() => void act(r, 'cancel')} aria-label={`Cancel job ${r.jobId}`}><CancelIcon fontSize="small" /></IconButton></span></Tooltip>
                )}
                {r.status !== 'queued' && r.status !== 'pending' && (
                  <Tooltip title="Reprint"><span><IconButton size="small" disabled={busy !== null} onClick={() => void act(r, 'reprint')} aria-label={`Reprint ${spec.noun} ${r.docNum}`}><ReplayIcon fontSize="small" /></IconButton></span></Tooltip>
                )}
              </Stack>
            ),
          },
        ]}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        "preparing" means PRPO is still reading the document from SAP and rendering it; "printing" means the office print
        agent has taken the job. A job stuck in printing means the agent stopped before it could report back. Check the
        printer before you reprint.
      </Typography>
    </Paper>
  );
}

// ---- Printer setup (Admin) ------------------------------------------------------------

const EMPTY_PRINTER = {
  name: '', agentId: '' as number | '', host: '', port: 9100, offsetXmm: 0, offsetYmm: 0, formLengthIn: 11,
  barcodeMode: 'native' as 'native' | 'graphic', quality: 'draft' as 'draft' | 'nlq', language: 'ESCP' as 'ESCP' | 'PDF',
  isActive: true,
};

function SetupTab({ companyId, notify, onChanged }: { companyId: number; notify: Notify; onChanged: () => void }) {
  const [printers, setPrinters] = useState<PrinterRow[]>([]);
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [edit, setEdit] = useState<{ id?: number; form: typeof EMPTY_PRINTER } | null>(null);
  const [newAgentOpen, setNewAgentOpen] = useState(false);
  const [newAgentName, setNewAgentName] = useState('');
  const [newToken, setNewToken] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, a]: any[] = await Promise.all([apiService.getInvoicePrinters(companyId), apiService.getPrintAgents()]);
      setPrinters(p.data || []);
      setAgents(a.data || []);
    } catch (e) {
      notify({ msg: errMsg(e, 'Could not load printer setup'), sev: 'error' });
    }
  }, [companyId, notify]);

  useEffect(() => { void load(); }, [load]);

  const savePrinter = async () => {
    if (!edit) return;
    setSaving(true);
    try {
      const f = edit.form;
      await apiService.saveInvoicePrinter(companyId, { ...f, agentId: f.agentId === '' ? null : f.agentId }, edit.id);
      setEdit(null);
      notify({ msg: 'Printer saved', sev: 'success' });
      await load();
      onChanged();
    } catch (e) {
      notify({ msg: errMsg(e, 'Could not save the printer'), sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const createAgent = async () => {
    setSaving(true);
    try {
      const r: any = await apiService.createPrintAgent(newAgentName);
      setNewToken(r.data.token);
      setNewAgentName('');
      await load();
    } catch (e) {
      notify({ msg: errMsg(e, 'Could not create the agent'), sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const toggleAgent = async (a: AgentRow) => {
    try {
      await apiService.setPrintAgentActive(a.agentId, !a.isActive);
      await load();
      onChanged();
    } catch (e) {
      notify({ msg: errMsg(e, 'Could not update the agent'), sev: 'error' });
    }
  };

  const [guideHtml, setGuideHtml] = useState<string | null>(null);
  const openGuide = async () => {
    try {
      const r: any = await apiService.getPrintAgentReadme();
      setGuideHtml(r.data.html);
    } catch (e) {
      notify({ msg: errMsg(e, 'Could not load the install guide'), sev: 'error' });
    }
  };

  const set = (patch: Partial<typeof EMPTY_PRINTER>) => setEdit((e) => (e ? { ...e, form: { ...e.form, ...patch } } : e));

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>Print agents</Typography>
          <Button startIcon={<MenuBookIcon />} onClick={() => void openGuide()}>Install guide</Button>
          <Button variant="outlined" onClick={() => { setNewToken(null); setNewAgentOpen(true); }}>Add agent</Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          The agent runs on a Windows server in the office and sends jobs to the printer.
        </Typography>
        <ResponsiveTable<AgentRow>
          ariaLabel="Print agents"
          rows={agents}
          getRowKey={(a) => a.agentId}
          emptyMessage="No agents yet."
          size="small"
          columns={[
            { id: 'name', header: 'Name', cell: (a) => a.name },
            { id: 'token', header: 'Token', cell: (a) => <code>{a.tokenPrefix}…</code>, hideOnMobile: true },
            {
              id: 'seen', header: 'Last seen',
              cell: (a) => <Chip size="small" color={agentOnline(a.lastSeenAt) ? 'success' : 'default'}
                label={a.lastSeenAt ? `${agentOnline(a.lastSeenAt) ? 'online' : 'offline'} · ${fmtDateTime(a.lastSeenAt)}` : 'never connected'} />,
            },
            { id: 'ip', header: 'From', cell: (a) => [a.lastIp, a.version && `v${a.version}`].filter(Boolean).join(' · ') || '—', hideOnMobile: true },
            {
              id: 'active', header: 'Enabled',
              cell: (a) => <Switch size="small" checked={a.isActive} onChange={() => void toggleAgent(a)} inputProps={{ 'aria-label': `Enable agent ${a.name}` }} />,
            },
          ]}
        />
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>Printers</Typography>
          <Button variant="outlined" onClick={() => setEdit({ form: { ...EMPTY_PRINTER, agentId: agents.length === 1 ? agents[0].agentId : '' } })}>Add printer</Button>
        </Stack>
        <ResponsiveTable<PrinterRow>
          ariaLabel="Printers"
          rows={printers}
          getRowKey={(p) => p.printerId}
          emptyMessage="No printers yet."
          size="small"
          onRowClick={(p) => setEdit({
            id: p.printerId,
            form: {
              name: p.name, agentId: p.agentId ?? '', host: p.host, port: p.port, offsetXmm: p.offsetXmm, offsetYmm: p.offsetYmm,
              formLengthIn: p.formLengthIn,
              barcodeMode: p.barcodeMode, quality: p.quality, language: p.language || 'ESCP', isActive: p.isActive,
            },
          })}
          columns={[
            { id: 'name', header: 'Name', cell: (p) => <>{p.name}{!p.isActive && <Chip size="small" label="inactive" sx={{ ml: 1 }} />}</> },
            { id: 'addr', header: 'Address', cell: (p) => `${p.host}:${p.port}` },
            { id: 'type', header: 'Type', cell: (p) => (p.language === 'PDF' ? 'Laser (A4)' : 'Dot-matrix (form)') },
            { id: 'agent', header: 'Agent', cell: (p) => p.agentName || <Typography variant="body2" color="error">none</Typography> },
            { id: 'cal', header: 'Offset X / Y (mm) · Form', cell: (p) => (p.language === 'PDF' ? '—' : `${p.offsetXmm} / ${p.offsetYmm} · ${p.formLengthIn}"`), hideOnMobile: true },
            { id: 'opts', header: 'Barcode · Quality', cell: (p) => (p.language === 'PDF' ? '—' : `${p.barcodeMode} · ${p.quality}`), hideOnMobile: true },
          ]}
        />
      </Paper>

      <Dialog open={!!edit} onClose={() => !saving && setEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{edit?.id ? 'Edit printer' : 'Add printer'}</DialogTitle>
        <DialogContent>
          {edit && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12} sm={6}>
                <TextField label="Name" fullWidth size="small" value={edit.form.name} onChange={(e) => set({ name: e.target.value })} placeholder="LQ-2190 Ardiya" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Print agent" fullWidth size="small" value={edit.form.agentId}
                  onChange={(e) => set({ agentId: e.target.value === '' ? '' : Number(e.target.value) })}>
                  <MenuItem value="">(none)</MenuItem>
                  {agents.map((a) => <MenuItem key={a.agentId} value={a.agentId}>{a.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={8}>
                <TextField label="Printer IP / host (as seen from the office)" fullWidth size="small" value={edit.form.host}
                  onChange={(e) => set({ host: e.target.value.trim() })} placeholder="192.168.1.50" />
              </Grid>
              <Grid item xs={4}>
                <TextField label="Port" type="number" fullWidth size="small" value={edit.form.port} onChange={(e) => set({ port: Number(e.target.value) })} />
              </Grid>
              <Grid item xs={12}>
                <TextField select label="Type" fullWidth size="small" value={edit.form.language}
                  onChange={(e) => set({ language: e.target.value as 'ESCP' | 'PDF' })}
                  helperText={edit.form.language === 'PDF' ? 'Plain A4 paper, sent as PDF: sales orders only.' : 'The pre-printed continuous form (Epson ESC/P2): invoices and sales orders.'}>
                  <MenuItem value="ESCP">Dot-matrix, pre-printed form</MenuItem>
                  <MenuItem value="PDF">Laser, plain A4 (PDF)</MenuItem>
                </TextField>
              </Grid>
              {edit.form.language !== 'PDF' && (<>
              <Grid item xs={6}>
                <TextField label="Offset X (mm, + right)" type="number" fullWidth size="small" inputProps={{ step: 0.5 }}
                  value={edit.form.offsetXmm} onChange={(e) => set({ offsetXmm: Number(e.target.value) })} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Offset Y (mm, + down)" type="number" fullWidth size="small" inputProps={{ step: 0.5 }}
                  value={edit.form.offsetYmm} onChange={(e) => set({ offsetYmm: Number(e.target.value) })} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Form length (inches)" type="number" fullWidth size="small" inputProps={{ step: 0.25, min: 3, max: 22 }}
                  value={edit.form.formLengthIn} onChange={(e) => set({ formLengthIn: Number(e.target.value) })}
                  helperText="One form, perforation to perforation: 11 for the Wooden Bakery form. If each page prints lower than the one before, this is too long." />
              </Grid>
              <Grid item xs={6}>
                <TextField select label="Barcode" fullWidth size="small" value={edit.form.barcodeMode}
                  onChange={(e) => set({ barcodeMode: e.target.value as 'native' | 'graphic' })}
                  helperText="Graphic if the printer skips the barcode">
                  <MenuItem value="native">Printer barcode (fast)</MenuItem>
                  <MenuItem value="graphic">Graphic</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField select label="Quality" fullWidth size="small" value={edit.form.quality}
                  onChange={(e) => set({ quality: e.target.value as 'draft' | 'nlq' })}>
                  <MenuItem value="draft">Draft (fast)</MenuItem>
                  <MenuItem value="nlq">Letter quality</MenuItem>
                </TextField>
              </Grid>
              </>)}
              <Grid item xs={12}>
                <FormControlLabel control={<Switch checked={edit.form.isActive} onChange={(e) => set({ isActive: e.target.checked })} />} label="Active" />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={() => void savePrinter()} disabled={saving}>Save</Button>
        </DialogActions>
      </Dialog>

      <ResponsiveDialog open={guideHtml !== null} onClose={() => setGuideHtml(null)} maxWidth="md" fullWidth>
        <DialogTitle>Print agent install guide</DialogTitle>
        <DialogContent dividers>
          {/* Rendered server-side from print-agent/README.md; every source character is HTML-escaped there. */}
          <Box
            sx={{
              '& h1': { typography: 'h5', mt: 0, mb: 1 },
              '& h2': { typography: 'h6', mt: 3, mb: 1 },
              '& h3': { typography: 'subtitle1', fontWeight: 600, mt: 2 },
              '& p, & li': { typography: 'body2', lineHeight: 1.7 },
              '& ul, & ol': { pl: 3, my: 1 },
              '& li': { mb: 0.75 },
              '& code': { fontFamily: 'monospace', fontSize: '0.85em', bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 },
              '& pre': { bgcolor: 'action.hover', p: 1.5, borderRadius: 1, overflowX: 'auto', my: 1 },
              '& pre code': { bgcolor: 'transparent', p: 0, fontSize: 12.5 },
            }}
            dangerouslySetInnerHTML={{ __html: guideHtml || '' }}
          />
        </DialogContent>
        <DialogActions><Button onClick={() => setGuideHtml(null)}>Close</Button></DialogActions>
      </ResponsiveDialog>

      <Dialog open={newAgentOpen} onClose={() => !saving && setNewAgentOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add print agent</DialogTitle>
        <DialogContent>
          {!newToken ? (
            <TextField autoFocus label="Name (e.g. the office server)" fullWidth size="small" sx={{ mt: 1 }}
              value={newAgentName} onChange={(e) => setNewAgentName(e.target.value)} />
          ) : (
            <Stack spacing={1} sx={{ mt: 1 }}>
              <Alert severity="warning">Copy this token now. It will not be shown again.</Alert>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField fullWidth size="small" value={newToken} InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }} />
                <Tooltip title="Copy">
                  <IconButton aria-label="Copy token" onClick={() => { void navigator.clipboard?.writeText(newToken); notify({ msg: 'Token copied', sev: 'info' }); }}>
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography variant="body2">On the office server, in an elevated PowerShell, in the <code>print-agent</code> folder:</Typography>
              <Box component="pre" sx={{ fontSize: 12, p: 1, bgcolor: 'background.default', overflowX: 'auto' }}>
                {`.\\install.ps1 -ServerUrl ${window.location.origin} -Token ${newToken}`}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {!newToken ? (
            <>
              <Button onClick={() => setNewAgentOpen(false)} disabled={saving}>Cancel</Button>
              <Button variant="contained" onClick={() => void createAgent()} disabled={saving || !newAgentName.trim()}>Create</Button>
            </>
          ) : (
            <Button variant="contained" onClick={() => { setNewAgentOpen(false); setNewToken(null); }}>Done</Button>
          )}
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export default SalesDocumentPrint;
